import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import {
  DataError,
  LoadingIndicator,
  useAlert,
  useDialog
} from "@digital-services/ui";
import { shortenOrgName } from "@ds-cps/ui";

import CountryDropDown from "@organization/components/CountryDropDown";
import { OrganizationExternalIds } from "@organization/components/OrganizationExternalIds";
import { OrganizationName } from "@organization/components/OrganizationName";
import OrganizationTypeDropDown from "@organization/components/OrganizationTypeDropDown";
import { useOrganizationContext } from "@organization/context/useOrganizationContext";
import { OrganizationStatus } from "@organization/models/OrganizationDetails";
import { useGetOrganization } from "@organization/services/use-get-organization";
import { useGetOrganizationExternalIds } from "@organization/services/use-get-organization-externalIds";
import { useUpdateOrganization } from "@organization/services/use-update-organization";

import { useUserContext } from "@hooks/useUserContext";

import { DeactivateOrganizationButton } from "./DeactivateOrganizationButton";

const OrganizationDetailsComponent: FC = () => {
  const { organizationId } = useParams();

  const { organization, isLoading, error } = useGetOrganization(
    organizationId ?? ""
  );

  const { user } = useUserContext();

  const { setOrganization } = useOrganizationContext();

  const { updateName, updateType, updateStatus, updateCountry } =
    useUpdateOrganization();

  const [organizationName, setOrganizationName] = useState<string>(
    organization?.name ?? ""
  );
  const [isValidOrganizationName, setIsValidOrganizationName] =
    useState<boolean>(false);

  const [countryCode, setCountryCode] = useState<string>(
    organization?.countryCode ?? ""
  );

  const [organizationType, setOrganizationType] = useState<string>(
    organization?.type ?? ""
  );

  const [status, setStatus] = useState<OrganizationStatus>();

  const { t } = useTranslation(undefined, { keyPrefix: "organization" });
  const { t: tCommon } = useTranslation();

  const { showDialog } = useDialog();
  const { notifySuccess, notifyError } = useAlert();

  const {
    externalIds,
    isLoading: isExternalIdsLoading,
    error: externalIdsError
  } = useGetOrganizationExternalIds(organizationId ?? "");

  const disableEditOrganization = externalIds?.some(item => {
    // because key can exist, but value can be not added yet, or cleared manually or something else.
    const isSalesforce = item.key == "Salesforce";
    const valueNotNull = item.value !== null;
    const valueNotEmpty =
      typeof item?.value === "string" && item?.value?.length > 0;

    return isSalesforce && valueNotNull && valueNotEmpty;
  });

  useEffect(() => {
    if (organization) {
      setStatus(organization.status);
      // This is after form changes to use latest refreshed organization data within context while switching between tabs
      setOrganization(organization);
    }
  }, [organization, setOrganization]);

  if (isLoading) {
    return (
      <div className="flex w-full items-center text-base">
        <LoadingIndicator label={t("load.message")} />
      </div>
    );
  }

  if (error) {
    return (
      <DataError
        title={t("load.error.message")}
        message=""
        className="text-left! text-red-600"
      />
    );
  }

  if (!organization) return null;

  function handleOrganizationNameChange(
    newValue: string,
    isValid: boolean
  ): void {
    setOrganizationName(newValue);
    setIsValidOrganizationName(isValid);
  }

  function handleOrganizationTypeChange(
    newValue: string,
    isValid: boolean
  ): void {
    setOrganizationType(newValue);
    if (isValid && organization) {
      updateType(organization.id, newValue)
        .then(() => {
          successAlert();
        })
        .catch(() => {
          errorAlert();
        });
    }
  }

  function handleCountryChange(newValue: string, isValid: boolean): void {
    setCountryCode(newValue);
    if (isValid && organization) {
      updateCountry(organization.id, newValue)
        .then(() => {
          successAlert();
        })
        .catch(() => {
          errorAlert();
        });
    }
  }

  function handleStatusChange(): void {
    const part =
      status === OrganizationStatus.InActive ? "activate" : "deactivate";

    const titleKey = `modify.status.${part}.confirm.title`;
    const contentKey = `modify.status.${part}.confirm.message`;

    showDialog({
      variant: "warning",
      header: t(titleKey, {
        organization: shortenOrgName(organization!.name, 30)
      }),
      content: t(contentKey),
      confirmButton: tCommon("button.confirm.label"),
      onConfirm: () => {
        const newStatus =
          status === OrganizationStatus.Active
            ? OrganizationStatus.InActive
            : OrganizationStatus.Active;
        updateStatus(organization!.id, newStatus)
          .then(() => {
            successAlert();
          })
          .catch(() => {
            errorAlert();
          });
      },
      cancelButton: tCommon("button.cancel.label"),
      onCancel: () => {
        // Do nothing
      },
      "data-testid": "update-organization-confirm-popup"
    });
  }

  function saveOrganizationName(): void {
    if (
      organization &&
      organization.name !== organizationName &&
      isValidOrganizationName
    ) {
      updateName(organization.id, organizationName)
        .then(() => {
          successAlert();
        })
        .catch(() => {
          errorAlert();
        });
    }
  }

  function errorAlert() {
    const name = shortenOrgName(organizationName, 30);
    notifyError({
      header: t("modify.error.message", { organization: name }),
      "data-testid": "update-organization-error-alert"
    });
  }

  function successAlert() {
    const name = shortenOrgName(organizationName, 30);
    notifySuccess({
      header: t("modify.success.message", { organization: name }),
      "data-testid": "update-organization-success-alert"
    });
  }

  return (
    <div className="flex w-full" data-testid="organization-detail-tab">
      <div className="grow">
        <div className="flex max-w-md flex-col gap-8 pt-5">
          <OrganizationName
            disabled={disableEditOrganization || user.isReader}
            label={t("name.label")}
            value={organizationName}
            placeholder={t("name.placeholder")}
            onInputChange={handleOrganizationNameChange}
            onBlur={saveOrganizationName}
          />
          <CountryDropDown
            disabled={disableEditOrganization || user.isReader}
            label={t("country.label")}
            selectedCountryCode={countryCode}
            onInputChange={handleCountryChange}
          />
          <OrganizationTypeDropDown
            disabled={disableEditOrganization || user.isReader}
            label={t("type.label")}
            selectedType={organizationType}
            onInputChange={handleOrganizationTypeChange}
          />

          <OrganizationExternalIds
            data={externalIds}
            isLoading={isExternalIdsLoading}
            error={externalIdsError}
          />
        </div>
      </div>
      <div className="m-5 justify-items-end">
        {user.isSuperAdmin ? (
          <DeactivateOrganizationButton
            status={status}
            disableEditOrganization={disableEditOrganization}
            onClick={handleStatusChange}
          />
        ) : null}
      </div>
    </div>
  );
};
export default OrganizationDetailsComponent;
